import React from 'react';
import { Heart, Clock, Users, Star, ChefHat } from 'lucide-react';
import { Card } from '../UI';
import { getRecipeImageURL } from '../../hooks/useRecipes';

const DIETARY_TAG_COLORS = {
  vegan: '#22c55e',
  vegetarian: '#86efac',
  pescatarian: '#3b82f6',
  keto: '#f97316',
  paleo: '#a16207',
  glutenfree: '#eab308',
  dairyfree: '#60a5fa',
  nutfree: '#c2410c',
  lowcarb: '#a855f7',
  halal: '#15803d',
  kosher: '#1d4ed8',
  highprotein: '#06b6d4',
};

const DIFFICULTY_COLORS = {
  easy: '#22c55e',
  medium: '#f97316',
  hard: '#ef4444',
};

const RecipeCard = ({ recipe, onClick, isFavorite, onToggleFavorite, isAuthenticated }) => {
  const imageURL = getRecipeImageURL(recipe);
  const totalTime = recipe.totalTimeMinutes
    || ((recipe.prepTimeMinutes || recipe.prepTime || 0) + (recipe.cookTimeMinutes || recipe.cookTime || 0))
    || null;
  const dietaryTags = (recipe.dietaryTags || []).slice(0, 2);
  const difficultyColor = DIFFICULTY_COLORS[recipe.difficulty?.toLowerCase()] || null;

  const handleFavorite = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      window.location.href = '/login';
      return;
    }
    onToggleFavorite && onToggleFavorite(recipe.firestoreId || recipe.id);
  };

  return (
    <Card
      className="group hover:shadow-lg transition-all relative overflow-hidden cursor-pointer p-0"
      onClick={onClick}
    >
      {/* Favorite button */}
      <button
        onClick={handleFavorite}
        className={`absolute top-md right-md z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-md hover:scale-110 ${
          isFavorite
            ? 'bg-error text-white'
            : 'bg-white/90 text-text-tertiary hover:text-error'
        }`}
      >
        <Heart size={16} className={isFavorite ? 'fill-current' : ''} />
      </button>

      {/* Image */}
      <div className="h-48 bg-gradient-to-br from-accent/10 to-primary/5 overflow-hidden relative flex items-center justify-center">
        {imageURL ? (
          <img
            src={imageURL}
            alt={recipe.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <ChefHat size={48} className="text-text-tertiary opacity-40" />
        )}

        {/* Difficulty badge */}
        {recipe.difficulty && difficultyColor && (
          <span
            className="absolute bottom-sm left-sm px-sm py-xs rounded-full text-white text-caption font-semibold"
            style={{ backgroundColor: difficultyColor }}
          >
            {recipe.difficulty.charAt(0).toUpperCase() + recipe.difficulty.slice(1)}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-md">
        {/* Meal type */}
        {recipe.mealType && (
          <span className="text-caption font-semibold text-text-tertiary uppercase tracking-wide">
            {recipe.mealType}
          </span>
        )}

        {/* Title */}
        <h3 className="text-callout font-semibold text-text-primary mt-xs mb-sm line-clamp-2 min-h-[2.5rem]">
          {recipe.title || recipe.name}
        </h3>

        {/* Meta row */}
        <div className="flex items-center gap-md text-caption text-text-secondary mb-sm">
          {recipe.rating > 0 && (
            <div className="flex items-center gap-xs">
              <Star size={12} className="text-accent fill-accent" />
              <span>{Number(recipe.rating).toFixed(1)}</span>
            </div>
          )}
          {totalTime > 0 && (
            <div className="flex items-center gap-xs">
              <Clock size={12} />
              <span>{totalTime} min</span>
            </div>
          )}
          {recipe.servings > 0 && (
            <div className="flex items-center gap-xs">
              <Users size={12} />
              <span>{recipe.servings}</span>
            </div>
          )}
        </div>

        {/* Dietary tags */}
        {dietaryTags.length > 0 && (
          <div className="flex flex-wrap gap-xs">
            {dietaryTags.map(tag => {
              const key = tag.toLowerCase().replace(/[_\s-]/g, '');
              return (
                <span
                  key={tag}
                  className="px-sm py-xs rounded-full text-caption font-medium text-white"
                  style={{ backgroundColor: DIETARY_TAG_COLORS[key] || '#6b7280' }}
                >
                  {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </span>
              );
            })}
          </div>
        )}

        {/* Author */}
        {recipe.authorName && (
          <p className="text-caption text-text-tertiary mt-sm truncate">
            by {recipe.authorName}
          </p>
        )}
      </div>
    </Card>
  );
};

export default RecipeCard;
